'use client';

import dynamic from 'next/dynamic';

import type { IIntroText } from 'components/IntroText';
import { useEffect, useState } from 'react';
import getSearchClient from 'server/repository/algolia/client';
import { DictionaryService } from 'service/dictionary/dictionaryService';

import type { IBreadcrumbTitles } from 'components/Breadcrumbs';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import type { ISearchOverlay } from 'components/HeaderNavigation/components/MobileNavigation';
import type { ISocialMediaComponent } from 'components/SocialMedia/types';
import type { ICardItem } from 'components/Subcategory';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import { TrackingService } from 'service/tracking/trackingService';
import TemplateMapper from 'templates/TemplateMapper';
import type { IGlobalMetaData } from 'types/GlobalMetadata';
import type { IHeroCarouselProps } from 'types/LandingPageData';
import type { IMetaData } from 'types/MetaData';
import { RecipeGridProvider } from '@/components/RecipeGrid/RecipeGridProvider';
import { RecipeGrid } from '@/components/RecipeGrid';
import { RecipeGridHeader } from '@/components/RecipeGrid/RecipeGridHeader';
import { RecipeGridNavigation } from '@/components/RecipeGrid/RecipeGridNavigation';

const Grid = dynamic(() => import('components/Grid').then((m: any) => m.Grid), {
  ssr: true,
}) as any;

const GridRow = dynamic(() => import('components/Grid').then((m: any) => m.GridRow), {
  ssr: true,
}) as any;

const IntroText = dynamic(() => import('components/IntroText').then((m) => m.IntroText), {
  ssr: true,
});

const Breadcrumbs = dynamic(
  () => import('components/Breadcrumbs').then((m: any) => m.Breadcrumbs),
  { ssr: true },
) as any;

const SocialMedia = dynamic(() => import('components/SocialMedia').then((m) => m.SocialMedia), {
  ssr: true,
});

const SliderCarousel = dynamic(
  () => import('components/SliderCarousel').then((m) => m.SliderCarousel),
  { ssr: true },
);

const Subcategory = dynamic(
  () => import('components/Subcategory').then((m: any) => m.Subcategory),
  { ssr: true },
) as any;

const Spacer = dynamic(() => import('components/Spacer').then((m) => m.Spacer), {
  ssr: true,
});

interface Category {
  recipeCategories: {
    slug: string;
    id: string;
    name: string;
    metaData: IMetaData;
    heroCarouselComponent: IHeroCarouselProps;
    introText: IIntroText;
    recipeSubcategorySuper: string;
    recipeSubcategoryTitle: string;
    recipeSubcategories?: {
      id: string;
      slug: string;
      productCategory: {
        categoryName: string;
        id: string;
      };
    }[];
  }[];
  recipesText: {
    value: string;
  };
  learnMoreText: {
    value: string;
  };
}

function ClientRecipesCategorySlugPage({
  locale,
  category,
  dynamicPageComponents,
  followUsData,
  breadcrumbsCMSData,
  searchOverlayData,
  refinementList,
  difficultyCopy,
  recipeCopy,
  globalMetadata,
  searchItemLabels,
  searchPageLabels,
  host,
  initialRecipes,
  initialTotalResults,
  initialFacets,
}: {
  locale: string;
  recipeEnv: string;
  category: Category;
  dynamicPageComponents: any;
  followUsData: ISocialMediaComponent;
  breadcrumbsCMSData: IBreadcrumbTitles;
  searchOverlayData: ISearchOverlay;
  difficultyCopy: Record<string, string>;
  recipeCopy: DictionaryItem[];
  refinementList: { [name: string]: string | string[] };
  globalMetadata: IGlobalMetaData;
  searchItemLabels: Record<string, string>;
  searchPageLabels: Record<string, string>;
  initialRecipes?: any[];
  initialTotalResults?: number;
  initialFacets?: Record<string, Record<string, number>>;
  host: string;
}) {
  const { pathTranslations, userToken } = useGlobalContext();

  const watchVideoLabel = DictionaryService.getCopy('see_video', recipeCopy);
  const heroCarouselData = category?.recipeCategories[0]?.heroCarouselComponent;
  const introData = category?.recipeCategories[0]?.introText;
  const categoryData = category?.recipeCategories[0] || {};
  const subcategoryData = {
    subHeading: categoryData?.recipeSubcategorySuper,
    heading: categoryData?.recipeSubcategoryTitle,
  };

  const breadcrumbsCategories = [
    {
      slug: pathTranslations.recipes,
      displayName: breadcrumbsCMSData?.recipesText?.value,
    },
  ];
  const subcategoryCardsData = categoryData?.recipeSubcategories?.map((subCategory: any) => {
    const amount = subCategory?.totalRecipes;
    // returns card data
    return {
      productsAmount: `${amount || 0} ${category?.recipesText?.value || 'recipes'}`,
      categoryId: subCategory?.id,
      category: categoryData.name,
      id: subCategory?.id,
      images: {
        desktop: subCategory?.promoImage?.asset?.[0]?.desktop,
        mobile: subCategory?.promoImage?.asset?.[0]?.mobile,
        mimeType: subCategory?.promoImage?.asset?.[0]?.mimeType,
      },
      title: subCategory?.recipeSubCategoryName,
      link: {
        url: `${pathTranslations.recipesSubCategory}${categoryData?.slug}/${subCategory?.slug}`,
        displayName: '',
      },
    };
  });

  const [searchOverlayDataExtended, setSearchOverlayDataExtended] = useState(searchOverlayData);

  useEffect(() => {
    if (!searchOverlayDataExtended.searchClient) {
      const algoliaClient = getSearchClient();
      setSearchOverlayDataExtended({
        ...searchOverlayDataExtended,
        searchClient: algoliaClient,
      });
    }
  }, [searchOverlayData]);

  useEffect(() => {
    TrackingService.contentGroups({ cg1: 'recipe categories', cg2: categoryData?.name || '' });
  }, []);

  /**
   * Used to fire the event for recipe cards in the Subcategory component
   */
  const subcategoryComponentClickContainer = (data: any) => {
    TrackingService.subcategoryComponentRecipeClick(data.category || 'na', data.title || 'na');
  };

  return (
    <>
      <Breadcrumbs
        titles={breadcrumbsCMSData}
        pageName={category?.recipeCategories[0]?.name}
        categories={breadcrumbsCategories}
      />
      {!heroCarouselData && <Spacer />}
      <div className="mx-auto flex flex-col items-center mb-40 relative overflow-x-hidden">
        {heroCarouselData && (
          <>
            <SliderCarousel
              bannerMasterbrands={heroCarouselData?.bannerMasterbrands}
              banners={heroCarouselData?.banners}
              isForBanners
              type="carousel"
              watchVideoLabel={watchVideoLabel}
            />
            <Spacer height={50} />
          </>
        )}
        {introData && (
          <>
            <IntroText
              super={introData.super ?? ''}
              title={introData.title ?? ''}
              description={introData.description ?? ''}
              titleTag={introData.titleTag ?? ''}
            />
            <Spacer />
          </>
        )}
        {subcategoryCardsData && !!subcategoryCardsData?.length && (
          <Subcategory
            titles={introData ? null : subcategoryData}
            cardData={subcategoryCardsData as ICardItem[]}
            subcategoryCardVariation="object"
            trackingEvent={subcategoryComponentClickContainer}
          />
        )}
        <>
          <Grid>
            <GridRow>
              <div className="mt-20 mb-5">
                <RecipeGridProvider
                  locale={locale}
                  refinementList={refinementList}
                  linkText={searchPageLabels?.see_recipe}
                  initialRecipes={initialRecipes}
                  initialTotalResults={initialTotalResults}
                  initialFacets={initialFacets}
                >
                  <RecipeGridHeader
                    title={category.recipeCategories?.[0].recipeSubcategoryTitle}
                    searchItemLabels={searchItemLabels}
                  />
                  <RecipeGrid
                    userToken={userToken}
                    host={host}
                    difficultyCopy={difficultyCopy}
                    categoryName={category?.recipeCategories?.[0]?.name}
                  />
                  <RecipeGridNavigation loadMoreLabel={searchPageLabels?.load_more} />
                </RecipeGridProvider>
              </div>
            </GridRow>
          </Grid>
        </>
      </div>
      {dynamicPageComponents && (
        <TemplateMapper
          components={dynamicPageComponents}
          copy={[]}
          globalMetadata={globalMetadata}
          recipeCopy={recipeCopy}
          difficultyCopy={difficultyCopy}
          searchItemLabels={searchItemLabels}
          searchData={searchOverlayDataExtended}
        />
      )}
      {followUsData && (
        <SocialMedia
          id={followUsData.id}
          super={followUsData.super}
          title={followUsData.title}
          socialMedia={followUsData.socialMedia}
          middlegroundImage={followUsData.middlegroundImage}
          foregroundImage={followUsData.foregroundImage}
        />
      )}
    </>
  );
}

export default ClientRecipesCategorySlugPage;
